import type { ArrayField, Config } from '@puckeditor/core';

const RAW_RICHTEXT_HTML_FIELD = { type: 'textarea' as const, label: 'Text' };
const RAW_FAQ_ANSWER_FIELD = { type: 'textarea' as const, label: 'Answer' };

const RAW_HTML_COMPONENTS = ['Heading', 'Paragraph'] as const;

type Components = NonNullable<Config['components']>;

/**
 * Puck 0.23's production `<Render>` replaces every `richtext` prop with a `RichTextRender` node
 * before calling the component. Heading, Text, and FAQ need the saved HTML string so they can
 * interpolate tenant variables before sanitising and applying their component-specific semantic boundary.
 *
 * Editor configs retain the real `richtext` fields. Production configs pass through this adapter,
 * changing only the field discriminator Puck scans while preserving every render and default.
 */
export function createProductionComponents(components: Components): Components {
  const withTopLevelRichText = RAW_HTML_COMPONENTS.reduce<Components>((adapted, componentName) => {
    const component = adapted[componentName] as typeof adapted[typeof componentName] | undefined;
    if (!component) return adapted;
    return {
      ...adapted,
      [componentName]: {
        ...component,
        fields: {
          ...component.fields,
          text: RAW_RICHTEXT_HTML_FIELD,
        },
      },
    };
  }, components);

  const faqComponent = withTopLevelRichText.Faq as typeof withTopLevelRichText[string] | undefined;
  if (!faqComponent?.fields) {
    return withTopLevelRichText;
  }

  const rowsField = faqComponent.fields.rows as ArrayField | undefined;

  if (!rowsField?.arrayFields) {
    return withTopLevelRichText;
  }

  return {
    ...withTopLevelRichText,
    Faq: {
      ...faqComponent,
      fields: {
        ...faqComponent.fields,
        rows: {
          ...rowsField,
          arrayFields: {
            ...rowsField.arrayFields,
            answer: RAW_FAQ_ANSWER_FIELD,
          },
        },
      },
    },
  };
}
